import React, { useState, useEffect, useCallback } from "react";
import { makeAuthenticatedRequest } from "../../utils/apiRequest";
import { useAuth } from "../../contexts/authContext";

// Update the component to accept days prop
const EntranceFeePopup = ({ isOpen, onClose, itineraryId, days = [] }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [editableEntranceFees, setEditableEntranceFees] = useState([]);
  const [apiResponseData, setApiResponseData] = useState(null);
  // Track currency per fee (default: $)
  const [currencyMap, setCurrencyMap] = useState({});

  // Helper to ensure each fee has a currency set (default to $)
  const ensureFeeCurrency = (fees) => {
    return fees.map((fee) => ({
      ...fee,
      currency: fee.currency || '$',
    }));
  };
  // Store USD rate for conversion (default 300 if not fetched)
  const [usdRate, setUsdRate] = useState(0); // fallback to 300 if not found
  // Store currency rates (for future use)
  const [currencyRates, setCurrencyRates] = useState([]);
  // Define fetchEntranceFees with useCallback to prevent recreation on each render
  const fetchEntranceFees = useCallback(async () => {
    if (!token || !itineraryId) return;
    
    console.log('EntranceFeePopup: Starting to fetch entrance fee data...');
    console.log('EntranceFeePopup: Using itinerary ID:', itineraryId);
    
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itinerary_id: itineraryId }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('EntranceFeePopup: API response received:', responseData);
      
      // Store the full response data in state for later use
      setApiResponseData(responseData);

      // The API response has data nested inside a 'data' array
      const itineraryData = responseData.data && responseData.data[0] ? responseData.data[0] : responseData;
      console.log('EntranceFeePopup: Extracted itinerary data:', itineraryData);

      // Extract USD rate from itineraryData.rates
      if (Array.isArray(itineraryData.rates)) {
        const usdObj = itineraryData.rates.find(r => r.currency_name && r.currency_name.includes("USD"));
        if (usdObj && usdObj.value) {
          setUsdRate(Number(usdObj.value));
        } else {
          setUsdRate(0); // fallback
        }
      } else {
        setUsdRate(0); // fallback
      }

      // Process the data from the itinerary_cost array which contains entrance fee information
      if (itineraryData.itinerary_cost && Array.isArray(itineraryData.itinerary_cost)) {
        console.log('EntranceFeePopup: Valid entrance fee data found in itinerary_cost:', itineraryData.itinerary_cost);
        
        // Process the API response data to match our component's expected format
        const formattedData = [];
        
        // Loop through each day in itinerary_cost
        itineraryData.itinerary_cost.forEach(dayData => {
          if (dayData.sightseeing_costing && Array.isArray(dayData.sightseeing_costing)) {
            // Get the day number and city
            const dayNumber = dayData.day?.replace('Day ', '') || '01';
            const city = dayData.city || '';
            
            // Loop through each sightseeing costing for this day
            dayData.sightseeing_costing.forEach(item => {
              // Skip entries that don't have proper data or are placeholders
              if (!item || !item.location_name) return;
              
              // Get the adult and child count from the actual response structure
              const adultCount = parseInt(item.location_visit_adults_n_children_count?.no_of_adults, 10) || 0;
              const childCount = parseInt(item.location_visit_adults_n_children_count?.no_of_children, 10) || 0;
              
              // Check if tax is available
              const taxAvailable = item.taxAvailable || false;
              
              // Get regular ticket prices (non-taxed prices)
              let originalAdultPrice = 0;
              let originalChildPrice = 0;
              let adultPrice = 0;
              let childPrice = 0;
              let taxedAdultPrice = 0;
              let taxedChildPrice = 0;
              let total = 0;
              
              // Extract original prices from the API response
              if (item.ticket_prices) {
                // Get regular ticket prices
                if (item.ticket_prices.adult_ticket_price) {
                  originalAdultPrice = parseFloat(item.ticket_prices.adult_ticket_price.value) || 0;
                }
                
                if (item.ticket_prices.children_ticket_price) {
                  originalChildPrice = parseFloat(item.ticket_prices.children_ticket_price.value) || 0;
                }
              }
              
              // Store the taxed prices if available
              if (item.taxed_prices) {
                taxedAdultPrice = parseFloat(item.taxed_prices.adult_with_tax) || 0;
                taxedChildPrice = parseFloat(item.taxed_prices.children_with_tax) || 0;
              } else if (taxAvailable) {
                // Calculate taxed prices if not available but tax is enabled
                const taxPercentage = itineraryData.taxes?.[0]?.tax_percentage || 0;
                taxedAdultPrice = originalAdultPrice * (1 + taxPercentage / 100);
                taxedChildPrice = originalChildPrice * (1 + taxPercentage / 100);
              }
              
              // Set the price based on tax availability
              if (taxAvailable) {
                adultPrice = taxedAdultPrice;
                childPrice = taxedChildPrice;
                // Use the total with tax if available
                total = parseFloat(item.with_tax_location_total_ticket_price) || 0;
              } else {
                adultPrice = originalAdultPrice;
                childPrice = originalChildPrice;
                // Use the regular total if available
                total = parseFloat(item.location_total_ticket_price) || 0;
              }
              
              // If total is not available from the API, calculate it
              if (total === 0) {
                total = (adultPrice * adultCount) + (childPrice * childCount);
              }
              
              // Add all locations to the formatted data
              formattedData.push({
                  day: dayNumber.padStart(2, '0'),
                  city: city,
                  sightseeing: item.location_name || '',
                  adultPrice: adultPrice,
                  adultCount: adultCount,
                  childPrice: childPrice,
                  childCount: childCount,
                  taxAvailable: taxAvailable,
                  // Store original non-taxed prices
                  originalAdultPrice: originalAdultPrice,
                  originalChildPrice: originalChildPrice,
                  // Store taxed prices
                  taxedAdultPrice: taxedAdultPrice,
                  taxedChildPrice: taxedChildPrice,
                  // Include any tax details if available
                  taxDetails: item.taxDetails || [],
                  tickets_country_category: item.tickets_country_category || 'all_other_countries',
                  // Use the provided total from API or calculated total
                  total: total,
                  // Preserve original ticket_prices and taxed_prices for currency detection
                  ticket_prices: item.ticket_prices || {},
                  taxed_prices: item.taxed_prices || {},
                  // Copy currency property if present
                  currency: item.currency || '$'
                });
            });
          }
        });
        
        console.log('EntranceFeePopup: Formatted data:', formattedData);
        
        // Filter out entrance fees for days that have been removed
        if (days && days.length > 0) {
          // Get the current day numbers from the days array
          const currentDayNumbers = days.map(day => day.number.toString().padStart(2, '0'));
          // Filter the formatted data to only include existing days
          let filteredData = formattedData.filter(item => {
            return currentDayNumbers.includes(item.day);
          });
          // Ensure currency is set
          filteredData = ensureFeeCurrency(filteredData);
          setEditableEntranceFees(filteredData);
        } else {
          // Ensure currency is set
          setEditableEntranceFees(ensureFeeCurrency(formattedData));
        }
      } else {
        console.log('EntranceFeePopup: No valid entrance fee data found in response');
        // If no data is returned or it's not in expected format, set empty array
        setEditableEntranceFees([]);
      }
    } catch (err) {
      console.error('EntranceFeePopup: Error fetching entrance fee data:', err.message);
      setError(err.message);
      setEditableEntranceFees([]);
    } finally {
      console.log('EntranceFeePopup: Entrance fee data fetch completed');
      setLoading(false);
    }
  }, [token, itineraryId, days]); // Add days to the dependency array



  // Load entrance fee data when component mounts and isOpen changes
  useEffect(() => {
    if (isOpen && itineraryId) {
      fetchEntranceFees();
    }
  }, [isOpen, itineraryId, fetchEntranceFees]);

  // Helper function to extract price from the ticket_prices object
  const extractPrice = (ticketPrices) => {
    if (!ticketPrices || Object.keys(ticketPrices).length === 0) return 0;
    
    // Check different country categories in order of priority
    const categories = ['all_other_countries', 'SAARC_country', 'locals'];
    
    for (const category of categories) {
      if (ticketPrices[category] && ticketPrices[category].adults) {
        // Extract numeric value from price string (e.g., "$10" -> 10)
        const price = parseFloat(ticketPrices[category].adults.replace(/[^0-9.]/g, ''));
        return isNaN(price) ? 0 : price;
      }
    }
    
    return 0;
  };
  
  // Helper function to extract child price
  const extractChildPrice = (ticketPrices, adultPrice) => {
    // For Dambulla Rock Temple - the UI shows $0 for children even though API indicates 50%
    // For consistency with UI, we'll return 0 for child price
    return 0;
  };

  // Sample data to use if no real data is available
  const sampleEntranceFees = [
    // {
    //   day: "01",
    //   city: "Colombo",
    //   sightseeing: "Lotus Tower",
    //   adultPrice: 10,
    //   adultCount: 2,
    //   childPrice: 5,
    //   childCount: 2,
    //   taxAvailable: false,
    //   originalAdultPrice: 10,
    //   originalChildPrice: 5,
    //   taxedAdultPrice: 11,  // Assuming 10% tax
    //   taxedChildPrice: 5.5, // Assuming 10% tax
    //   total: 30 // (10*2) + (5*2) = 30
    // }
  ];

  // Use fetched and editable data or sample data if empty
  const feesData = editableEntranceFees.length > 0 ? editableEntranceFees : sampleEntranceFees;
  
  // Ensure all fees have a total property and all numeric values are properly set
  feesData.forEach(fee => {
    fee.adultPrice = parseFloat(fee.adultPrice || 0);
    fee.childPrice = parseFloat(fee.childPrice || 0);
    fee.adultCount = parseInt(fee.adultCount || 0, 10);
    fee.childCount = parseInt(fee.childCount || 0, 10);
    if (fee.total === undefined || fee.total === null || isNaN(fee.total) || fee.total === 0) {
      fee.total = (fee.adultPrice * fee.adultCount) + (fee.childPrice * fee.childCount);
    } else {
      fee.total = parseFloat(fee.total);
      if (isNaN(fee.total)) {
        fee.total = (fee.adultPrice * fee.adultCount) + (fee.childPrice * fee.childCount);
      }
    }
  });

  // Automatically recalculate totals whenever adultCount, childCount, adultPrice, or childPrice changes
  useEffect(() => {
    setEditableEntranceFees(prevFees => {
      // Only recalculate if something actually changed
      const updatedFees = prevFees.map(fee => {
        const adultPrice = parseFloat(fee.adultPrice || 0);
        const childPrice = parseFloat(fee.childPrice || 0);
        const adultCount = parseInt(fee.adultCount || 0, 10);
        const childCount = parseInt(fee.childCount || 0, 10);
        const newTotal = (adultPrice * adultCount) + (childPrice * childCount);
        // Only update if total is different
        if (fee.total !== newTotal) {
          return { ...fee, total: newTotal };
        }
        return fee;
      });
      return updatedFees;
    });
    // Only run when these fields change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(editableEntranceFees.map(fee => ({
    adultCount: fee.adultCount,
    childCount: fee.childCount,
    adultPrice: fee.adultPrice,
    childPrice: fee.childPrice
  })))]);
  
  // Handler for changing adult quantity
  const handleAdultQuantityChange = (feeIndex, newQuantity) => {
    // Create a deep copy of the data
    const updatedFees = JSON.parse(JSON.stringify(editableEntranceFees));
    
    // Ensure the quantity is a positive number
    newQuantity = Math.max(0, parseInt(newQuantity, 10) || 0);
    
    // Update the adult quantity
    updatedFees[feeIndex].adultCount = newQuantity;
    // Mark as manually updated
    updatedFees[feeIndex].manually_updated = true;
    // Recalculate the total
    updatedFees[feeIndex].total = (updatedFees[feeIndex].adultPrice * newQuantity) + 
                                  (updatedFees[feeIndex].childPrice * updatedFees[feeIndex].childCount);
    
    // Update state
    setEditableEntranceFees(updatedFees);
  };
  
  // Handler for changing child quantity
  const handleChildQuantityChange = (feeIndex, newQuantity) => {
    // Create a deep copy of the data
    const updatedFees = JSON.parse(JSON.stringify(editableEntranceFees));
    
    // Ensure the quantity is a positive number
    newQuantity = Math.max(0, parseInt(newQuantity, 10) || 0);
    
    // Update the child quantity
    updatedFees[feeIndex].childCount = newQuantity;
    // Mark as manually updated
    updatedFees[feeIndex].manually_updated = true;
    // Recalculate the total
    updatedFees[feeIndex].total = (updatedFees[feeIndex].adultPrice * updatedFees[feeIndex].adultCount) + 
                                  (updatedFees[feeIndex].childPrice * newQuantity);
    
    // Update state
    setEditableEntranceFees(updatedFees);
  };
  
  // Get tax percentage from API response
  const getTaxPercentage = () => {
    const itineraryData = apiResponseData?.data?.[0] || apiResponseData;
    if (itineraryData && itineraryData.taxes && itineraryData.taxes.length > 0) {
      return itineraryData.taxes[0].tax_percentage || 0;
    }
    return 0;
  };
  
  // Calculate price with tax
  const calculatePriceWithTax = (price, taxAvailable) => {
    if (!taxAvailable) return price;
    
    const taxPercentage = getTaxPercentage();
    return price * (1 + taxPercentage / 100);
  };
  
  // Handler for toggling currency per sightseeing
  const handleCurrencyToggle = (feeIndex) => {
    setEditableEntranceFees((oldFees) => {
      const updated = [...oldFees];
      const current = updated[feeIndex].currency || '$';
      const newCurrency = current === '$' ? 'RS' : '$';
      updated[feeIndex] = { ...updated[feeIndex], currency: newCurrency, manually_updated: true };
      return updated;
    });
  };

  // Handler for toggling tax availability
  const handleTaxToggle = (feeIndex) => {
    // Create a deep copy of the data
    const updatedFees = JSON.parse(JSON.stringify(editableEntranceFees));
    
    // Toggle the tax availability
    updatedFees[feeIndex].taxAvailable = !updatedFees[feeIndex].taxAvailable;
    // Mark as manually updated
    updatedFees[feeIndex].manually_updated = true;
    
    // Get the pricing data from API response if available
    const itineraryData = apiResponseData?.data?.[0] || apiResponseData;
    const taxPercentage = itineraryData?.taxes?.[0]?.tax_percentage || 0;
    
    // Store the original prices before toggling if they don't exist
    if (updatedFees[feeIndex].originalAdultPrice === undefined) {
      updatedFees[feeIndex].originalAdultPrice = updatedFees[feeIndex].adultPrice;
    }
    
    if (updatedFees[feeIndex].originalChildPrice === undefined) {
      updatedFees[feeIndex].originalChildPrice = updatedFees[feeIndex].childPrice;
    }
    
    // Update prices based on tax availability
    if (updatedFees[feeIndex].taxAvailable) {
      // With tax - use taxed prices if available or calculate
      if (updatedFees[feeIndex].taxedAdultPrice !== undefined) {
        // Use cached taxed prices if available
        updatedFees[feeIndex].adultPrice = updatedFees[feeIndex].taxedAdultPrice;
        updatedFees[feeIndex].childPrice = updatedFees[feeIndex].taxedChildPrice;
      } else {
        // Calculate taxed prices and cache them
        const adultPriceWithTax = updatedFees[feeIndex].originalAdultPrice * (1 + taxPercentage / 100);
        const childPriceWithTax = updatedFees[feeIndex].originalChildPrice * (1 + taxPercentage / 100);
        
        updatedFees[feeIndex].taxedAdultPrice = adultPriceWithTax;
        updatedFees[feeIndex].taxedChildPrice = childPriceWithTax;
        
        updatedFees[feeIndex].adultPrice = adultPriceWithTax;
        updatedFees[feeIndex].childPrice = childPriceWithTax;
      }
    } else {
      // Without tax - restore original prices
      updatedFees[feeIndex].adultPrice = updatedFees[feeIndex].originalAdultPrice;
      updatedFees[feeIndex].childPrice = updatedFees[feeIndex].originalChildPrice;
    }
    
    // Recalculate the total
    updatedFees[feeIndex].total = (updatedFees[feeIndex].adultPrice * updatedFees[feeIndex].adultCount) + 
                                  (updatedFees[feeIndex].childPrice * updatedFees[feeIndex].childCount);
    
    // Update state
    setEditableEntranceFees(updatedFees);
  };
  
  // Handler for changing adult price
  const handleAdultPriceChange = (feeIndex, newPrice) => {
    // Create a deep copy of the data
    const updatedFees = JSON.parse(JSON.stringify(editableEntranceFees));
    
    // Ensure the price is a positive number
    newPrice = Math.max(0, parseFloat(newPrice) || 0);
    
    const taxPercentage = getTaxPercentage();
    const taxAvailable = updatedFees[feeIndex].taxAvailable;
    
    // If we're currently showing taxed prices, we need to update both original and taxed prices
    if (taxAvailable) {
      // We're editing the taxed price directly, so calculate the original price
      // For a tax of 30%, the original price would be taxedPrice / 1.3
      updatedFees[feeIndex].taxedAdultPrice = newPrice;
      updatedFees[feeIndex].originalAdultPrice = newPrice / (1 + taxPercentage / 100);
    } else {
      // We're editing the original price, so update it and calculate the taxed price
      // For a tax of 30%, the taxed price would be originalPrice * 1.3
      updatedFees[feeIndex].originalAdultPrice = newPrice;
      updatedFees[feeIndex].taxedAdultPrice = newPrice * (1 + taxPercentage / 100);
    }
    
    // Update the displayed price
    updatedFees[feeIndex].adultPrice = newPrice;
    // Mark as manually updated
    updatedFees[feeIndex].manually_updated = true;
    
    // Get counts for total calculation
    const adultCount = updatedFees[feeIndex].adultCount;
    const childCount = updatedFees[feeIndex].childCount;
    const childPrice = updatedFees[feeIndex].childPrice;
    
    // Recalculate the total
    updatedFees[feeIndex].total = (newPrice * adultCount) + (childPrice * childCount);
    
    // Update state
    setEditableEntranceFees(updatedFees);
  };
  
  // Handler for changing child price
  const handleChildPriceChange = (feeIndex, newPrice) => {
    // Create a deep copy of the data
    const updatedFees = JSON.parse(JSON.stringify(editableEntranceFees));
    
    // Ensure the price is a positive number
    newPrice = Math.max(0, parseFloat(newPrice) || 0);
    
    const taxPercentage = getTaxPercentage();
    const taxAvailable = updatedFees[feeIndex].taxAvailable;
    
    // If we're currently showing taxed prices, we need to update both original and taxed prices
    if (taxAvailable) {
      // We're editing the taxed price directly, so calculate the original price
      // For a tax of 30%, the original price would be taxedPrice / 1.3
      updatedFees[feeIndex].taxedChildPrice = newPrice;
      updatedFees[feeIndex].originalChildPrice = newPrice / (1 + taxPercentage / 100);
    } else {
      // We're editing the original price, so update it and calculate the taxed price
      // For a tax of 30%, the taxed price would be originalPrice * 1.3
      updatedFees[feeIndex].originalChildPrice = newPrice;
      updatedFees[feeIndex].taxedChildPrice = newPrice * (1 + taxPercentage / 100);
    }
    
    // Update the displayed price
    updatedFees[feeIndex].childPrice = newPrice;
    // Mark as manually updated
    updatedFees[feeIndex].manually_updated = true;
    
    // Get counts for total calculation
    const adultCount = updatedFees[feeIndex].adultCount;
    const childCount = updatedFees[feeIndex].childCount;
    const adultPrice = updatedFees[feeIndex].adultPrice;
    
    // Recalculate the total
    updatedFees[feeIndex].total = (adultPrice * adultCount) + (newPrice * childCount);
    
    // Update state
    setEditableEntranceFees(updatedFees);
  };
  
  // Function to save changes to the API
  const saveChanges = async () => {
    // Ensure all fees have a currency property set before saving
    setEditableEntranceFees((oldFees) => ensureFeeCurrency(oldFees, currencyMap));
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      setUpdating(true);
      // Deep clone the full itinerary data
      const itineraryData = apiResponseData?.data?.[0] || apiResponseData || {};
      const updatedItineraryData = JSON.parse(JSON.stringify(itineraryData));
      const taxPercentage = itineraryData.taxes?.[0]?.tax_percentage || 0;

      // Group entrance fees by day and city
      const entranceFeesByDayCity = {};
      editableEntranceFees.forEach(fee => {
        const dayKey = `Day ${fee.day.toString().padStart(2, '0')}`;
        if (!entranceFeesByDayCity[dayKey]) entranceFeesByDayCity[dayKey] = {};
        if (!entranceFeesByDayCity[dayKey][fee.city]) entranceFeesByDayCity[dayKey][fee.city] = [];
        entranceFeesByDayCity[dayKey][fee.city].push(fee);
      });

      // Only update sightseeing_costing for matching day/city, keep all other costing data
      if (Array.isArray(updatedItineraryData.itinerary_cost)) {
        updatedItineraryData.itinerary_cost.forEach(dayCost => {
          const day = dayCost.day;
          const city = dayCost.city;
          if (
            entranceFeesByDayCity[day] &&
            entranceFeesByDayCity[day][city]
          ) {
            const fees = entranceFeesByDayCity[day][city];
            let dayTotalCost = 0;
            let dayTotalCostWithTax = 0;
            const sightseeing_costing = fees.map(fee => {
              const totalWithoutTax = (fee.originalAdultPrice * fee.adultCount) + (fee.originalChildPrice * fee.childCount);
              dayTotalCost += totalWithoutTax;
              const sightseeingItem = {
                location_name: fee.sightseeing,
                location_visit_adults_n_children_count: {
                  no_of_adults: fee.adultCount.toString(),
                  no_of_children: fee.childCount.toString()
                },
                ticket_prices: {
                  adult_ticket_price: {
                    currency: fee.currency || '$',
                    value: fee.originalAdultPrice.toFixed(2)
                  },
                  children_ticket_price: {
                    currency: fee.currency || '$',
                    value: fee.originalChildPrice.toFixed(2)
                  }
                },
                taxAvailable: fee.taxAvailable,
                taxDetails: fee.taxDetails || [],
                tickets_country_category: fee.tickets_country_category || 'all_other_countries',
                location_total_ticket_price: totalWithoutTax.toFixed(2),
                currency: fee.currency || '$'
              };
              // Add manually_updated flag if set
              if (fee.manually_updated) {
                sightseeingItem.manually_updated = true;
              }
              // Always calculate taxed prices
              const adultWithTax = fee.originalAdultPrice * (1 + taxPercentage / 100);
              const childWithTax = fee.originalChildPrice * (1 + taxPercentage / 100);
              const totalWithTax = (adultWithTax * fee.adultCount) + (childWithTax * fee.childCount);
              dayTotalCostWithTax += totalWithTax;
              sightseeingItem.taxed_prices = {
                adult_with_tax: adultWithTax.toFixed(2),
                children_with_tax: childWithTax.toFixed(2)
              };
              sightseeingItem.with_tax_location_total_ticket_price = totalWithTax.toFixed(2);
              return sightseeingItem;
            });
            dayCost.sightseeing_costing = sightseeing_costing;
            dayCost.day_total_cost = dayTotalCost.toFixed(2);
            dayCost.day_total_cost_with_tax = dayTotalCostWithTax.toFixed(2);
            dayCost.taxes = itineraryData.taxes || [];
          }
          // All other costing fields (transport_costing, accommodation_costing, etc.) remain unchanged
        });
      }

      // --- Sync entrance fee changes to the days array as well ---
      if (Array.isArray(updatedItineraryData.days)) {
        editableEntranceFees.forEach(fee => {
          // Find the matching day
          const dayObj = updatedItineraryData.days.find(
            d => String(d.number).padStart(2, '0') === fee.day
          );
          if (dayObj && Array.isArray(dayObj.citiesWithActivities)) {
            // Find the matching city
            const cityObj = dayObj.citiesWithActivities.find(
              c => c.city === fee.city
            );
            if (cityObj && Array.isArray(cityObj.activities)) {
              // Find the matching sightseeing activity
              const activityObj = cityObj.activities.find(
                a => a.location_name === fee.sightseeing
              );
              if (activityObj) {
                // Update ticket price, counts, tax, etc.
                activityObj.ticketPrice = {
                  adults: String(fee.adultPrice),
                  children: String(fee.childPrice)
                };
                activityObj.ticketCategory = fee.tickets_country_category || 'all_other_countries';
                activityObj.taxAvailable = fee.taxAvailable;
                activityObj.taxDetails = fee.taxDetails || [];
                activityObj.adultCount = fee.adultCount;
                activityObj.childCount = fee.childCount;
                // Optionally update other fields as needed
              }
            }
          }
        });
      }
      // Prepare the payload
      const apiPayload = {
        itinerary_id: itineraryId,
        updates: updatedItineraryData,
        price_change: true
      };

      console.log('EntranceFeePopup: Formatted API payload:', apiPayload);
      // Make the API call to update the itinerary data
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/update_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update entrance fees: ${response.status} - ${errorData.error || "Unknown error"}`);
      }
      const result = await response.json();
      console.log('EntranceFeePopup: Successfully updated entrance fees:', result);
      // alert("Entrance fees have been successfully updated!");
      onClose();
    } catch (error) {
      console.error('EntranceFeePopup: Error saving entrance fees:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };


  // Calculate the total
  const calculateTotal = () => {
    let total = 0;
    feesData.forEach(fee => {
      // Make sure we're adding a valid number
      const feeTotal = parseFloat(fee.total || 0);
      if (!isNaN(feeTotal)) {
        total += feeTotal;
      }
    });
    return total;
  };
  
  // Format numbers to 2 decimal places with safety check
  const formatCurrency = (value) => {
    // Ensure value is a number and handle null/undefined
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
  };
  

  
  // Convert amount based on currency
  const convertAmount = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    if (fromCurrency === '$' && toCurrency === 'RS') {
      return amount * usdRate;
    } else if (fromCurrency === 'RS' && toCurrency === '$') {
      return amount / usdRate;
    }
    
    return amount;
  };
  
  // Get display amount based on selected currency
  const getDisplayAmount = (fee, feeIndex) => {
    const currency = currencyMap[`${feeIndex}`] || '$';
    if (currency === 'RS') {
      return convertAmount(fee.total, '$', 'RS');
    }
    return fee.total;
  };

  if (!isOpen) return null;
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Loading entrance fee data...</p>
        </div>
      </div>
    );
  }

  if (updating) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Updating Entrance Fee Data...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <h2 className="text-red-600 text-lg font-semibold mb-2">Error</h2>
          <p className="text-gray-800 mb-4">{error}</p>
          <button 
            onClick={onClose}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out">
      <div 
        className="bg-white border border-blue-200 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] relative transform transition-all duration-300 ease-in-out overflow-hidden flex flex-col" 
        style={{ 
          background: 'linear-gradient(to bottom right, #ffffff, #f7fbff)',
          boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 8px 10px -6px rgba(59, 130, 246, 0.3)'
        }}>
        {/* Close icon */}
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 text-blue-500 hover:text-blue-700 transition-colors z-10"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header */}
        <div className="p-4 border-b border-blue-200 sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="text-blue-800 text-center font-bold text-xl uppercase tracking-wider">ENTRANCE FEES</h2>
        </div>
        
        {/* Scrollable table container */}   
        <div className="flex-1 overflow-hidden p-4">
          <div className="overflow-auto max-h-[calc(90vh-180px)] rounded-lg shadow-inner">
            <table className="w-full border-collapse">
              {/* Sticky Header */}
              <thead className="sticky top-0 z-10">
                <tr className="bg-blue-100">
                  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">Day</th>
                  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">City</th>
                  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">Sightseeing</th>
                  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">Tax</th>
                  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">No Of Members</th>
                  <th className="border border-blue-200 p-3 text-blue-700 text-right font-medium text-xs uppercase tracking-wider sticky top-0">$</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {feesData.map((fee, feeIndex) => {
                  // Use fee.currency as the only source of truth
                  const rowCurrency = fee.currency || '$';
                  return (
                  <tr 
                    key={`fee-${feeIndex}`} 
                    className={`${feeIndex % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-blue-100 transition-colors`}
                  >
                    <td className="border border-blue-200 p-3 text-gray-800 text-center">
                      {fee.day}
                    </td>
                    <td className="border border-blue-200 p-3 text-gray-800 text-center">
                      {fee.city}
                    </td>
                    <td className="border border-blue-200 p-3 text-gray-800 text-center">
                      {fee.sightseeing}
                    </td>
                    <td className="border border-blue-200 p-3 text-gray-800 text-center">
                      <div className="flex items-center justify-center">
                        <input 
                          type="checkbox" 
                          checked={fee.taxAvailable} 
                          onChange={() => handleTaxToggle(feeIndex)} 
                          className="h-4 w-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
                        />
                      </div>
                    </td>
                    <td className="border border-blue-200 p-3 text-gray-800">
                      <div className="flex flex-col space-y-3 items-center">
                        {/* Adult quantity and price row */}
                        <div className="flex items-center justify-center w-full space-x-2">
                          <div className="flex items-center">
                            <span className="mr-1">{rowCurrency}</span>
                            <input 
                              type="number" 
                              min="0" 
                              step="0.01"
                              value={(() => {
                                let val = rowCurrency === 'RS' ? convertAmount(fee.adultPrice, '$', 'RS') : fee.adultPrice;
                                if (val === null || val === undefined || val === '' || isNaN(val) || Number(val) === 0) {
                                  return '';
                                }
                                return String(val);
                              })()}
                              onChange={(e) => {
                                let inputValue = parseFloat(e.target.value) || 0;
                                // Always store in USD internally
                                if (rowCurrency === 'RS') {
                                  inputValue = inputValue / usdRate;
                                }
                                handleAdultPriceChange(feeIndex, inputValue);
                              }}
                              className="w-16 text-center border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                            />
                            <span className="ml-1 text-sm font-medium">Adult</span>
                          </div>
                          <span>X</span>
                          <input 
                            type="number" 
                            min="0" 
                            value={fee.adultCount} 
                            onChange={(e) => handleAdultQuantityChange(feeIndex, e.target.value)}
                            className="w-16 text-center border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                          />
                        </div>
                        {/* Child quantity and price row */}
                        <div className="flex items-center justify-center w-full space-x-2">
                          <div className="flex items-center">
                            <span className="mr-1">{rowCurrency}</span>
                            <input 
                              type="number" 
                              min="0" 
                              step="0.01"
                              value={(() => {
                                let val = rowCurrency === 'RS' ? convertAmount(fee.childPrice, '$', 'RS') : fee.childPrice;
                                if (val === null || val === undefined || val === '' || isNaN(val) || Number(val) === 0) {
                                  return '';
                                }
                                return String(val);
                              })()}
                              onChange={(e) => {
                                let inputValue = parseFloat(e.target.value) || 0;
                                // Always store in USD internally
                                if (rowCurrency === 'RS') {
                                  inputValue = inputValue / usdRate;
                                }
                                handleChildPriceChange(feeIndex, inputValue);
                              }}
                              className="w-16 text-center border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                            />
                            <span className="ml-1 text-sm font-medium">Child</span>
                          </div>
                          <span>X</span>
                          <input 
                            type="number" 
                            min="0" 
                            value={fee.childCount} 
                            onChange={(e) => handleChildQuantityChange(feeIndex, e.target.value)}
                            className="w-16 text-center border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                          />
                        </div>
                      </div>
                    </td>
                    <td className="border border-blue-200 p-3 text-gray-800 text-right font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* Toggle switch for currency */}
                        <button
                          type="button"
                          onClick={() => handleCurrencyToggle(feeIndex)}
                          className={`relative w-12 h-6 flex items-center rounded-full focus:outline-none transition border border-blue-300 ${rowCurrency === '$' ? 'bg-blue-200' : 'bg-green-200'}`}
                          title="Toggle currency"
                          style={{ minWidth: 48 }}
                        >
                          <span className={`absolute left-1 text-xs font-bold transition-colors ${rowCurrency === '$' ? 'text-blue-900' : 'text-gray-400'}`}>$</span>
                          <span className={`absolute right-1 text-xs font-bold transition-colors ${rowCurrency === 'RS' ? 'text-green-900' : 'text-gray-400'}`}>RS</span>
                          <span
                            className={`absolute transition-transform duration-200 ease-in-out bg-white h-5 w-5 rounded-full shadow ${rowCurrency === '$' ? 'translate-x-0' : 'translate-x-6'}`}
                            style={{ left: 0, top: 0.5 }}
                          ></span>
                        </button>
                        <div>
                          {rowCurrency} {formatCurrency(rowCurrency === 'RS' ? convertAmount(fee.total, '$', 'RS') : fee.total)}
                          {fee.taxAvailable && getTaxPercentage() > 0 && (
                            <div className="text-xs text-blue-600">
                              (incl. {getTaxPercentage()}% tax)
                            </div>
                          )}
                          {/* Show opposite currency for reference */}
                          {rowCurrency === 'RS' && (
                            <div className="text-xs text-gray-500 mt-1 block text-right" style={{lineHeight:1}}>
                              <span>$ {formatCurrency(fee.total)}</span>
                            </div>
                          )}
                          {rowCurrency === '$' && (
                            <div className="text-xs text-gray-500 mt-1 block text-right" style={{lineHeight:1}}>
                              <span>RS {formatCurrency(convertAmount(fee.total, '$', 'RS'))}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )})}
                <tr className="bg-blue-100 sticky bottom-0 shadow-md">
                  <td colSpan="5" className="border border-blue-200 p-3 text-blue-800 text-center font-semibold">
                    Total
                  </td>
                  <td className="border border-blue-200 p-3 text-blue-800 text-right font-semibold">
                    $ {formatCurrency(calculateTotal())}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Save and Cancel buttons - fixed at bottom */}
        <div className="p-4 border-t border-blue-200 flex justify-end space-x-3 bg-white sticky bottom-0 shadow-md z-10">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntranceFeePopup;
